import { hann } from './fft';

/**
 * Time / pitch manipulation on raw channel data.
 *
 * Stretching uses WSOLA (waveform-similarity overlap-add): for each output
 * frame we hunt around the nominal read position for the offset that best
 * continues the previous frame, which keeps transients from smearing the way
 * a naive OLA does. Pitch shifting is stretch-then-resample.
 */

const FRAME = 1024;
const SYNTH_HOP = FRAME >> 2;
const SEARCH = 256;

function bestOffset(
  input: Float32Array,
  templateStart: number,
  candidate: number,
  len: number,
): number {
  if (templateStart < 0) return 0;
  let bestScore = -Infinity;
  let best = 0;
  const step = 2; // half-resolution scan is plenty and twice as fast
  for (let d = -SEARCH; d <= SEARCH; d += step) {
    const pos = candidate + d;
    if (pos < 0 || pos + len >= input.length) continue;
    let acc = 0;
    for (let i = 0; i < len; i += 2) {
      acc += input[templateStart + i] * input[pos + i];
    }
    if (acc > bestScore) {
      bestScore = acc;
      best = d;
    }
  }
  return best;
}

/**
 * Stretches `channels` by `ratio` (2 = twice as long). The similarity search
 * runs once on a mono sum and the resulting offsets are applied to every
 * channel, so the stereo image stays intact.
 */
export function timeStretch(channels: Float32Array[], ratio: number): Float32Array[] {
  if (Math.abs(ratio - 1) < 1e-4) return channels.map((c) => c.slice());
  const inLen = channels[0].length;
  if (inLen < FRAME * 2) {
    // Too short for WSOLA to say anything meaningful — resample instead.
    return channels.map((c) => resample(c, Math.max(1, Math.round(inLen * ratio))));
  }
  const outLen = Math.max(FRAME, Math.round(inLen * ratio));
  const win = hann(FRAME);
  const analysisHop = SYNTH_HOP / ratio;

  const mono = new Float32Array(inLen);
  for (const c of channels) for (let i = 0; i < inLen; i++) mono[i] += c[i];

  const outs = channels.map(() => new Float32Array(outLen + FRAME));
  const norm = new Float32Array(outLen + FRAME);

  let prevRead = 0;
  let outPos = 0;
  let frame = 0;
  while (outPos + FRAME < outLen + FRAME) {
    const nominal = Math.round(frame * analysisHop);
    let read = nominal;
    if (frame > 0) {
      const template = prevRead + SYNTH_HOP;
      if (template + FRAME < inLen) {
        read = nominal + bestOffset(mono, template, nominal, FRAME >> 1);
      }
    }
    read = Math.max(0, Math.min(inLen - FRAME - 1, read));

    for (let ch = 0; ch < channels.length; ch++) {
      const src = channels[ch];
      const dst = outs[ch];
      for (let i = 0; i < FRAME; i++) dst[outPos + i] += src[read + i] * win[i];
    }
    for (let i = 0; i < FRAME; i++) norm[outPos + i] += win[i];

    prevRead = read;
    outPos += SYNTH_HOP;
    frame++;
    if (nominal + FRAME >= inLen) break;
  }

  return outs.map((o) => {
    const trimmed = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const w = norm[i];
      trimmed[i] = w > 1e-4 ? o[i] / w : o[i];
    }
    return trimmed;
  });
}

/** Cubic-interpolated resample to an explicit length. */
export function resample(input: Float32Array, outLen: number): Float32Array {
  const out = new Float32Array(outLen);
  const scale = (input.length - 1) / Math.max(1, outLen - 1);
  for (let i = 0; i < outLen; i++) {
    const p = i * scale;
    const i1 = Math.floor(p);
    const f = p - i1;
    const i0 = Math.max(0, i1 - 1);
    const i2 = Math.min(input.length - 1, i1 + 1);
    const i3 = Math.min(input.length - 1, i1 + 2);
    const a = input[i0];
    const b = input[i1];
    const c = input[i2];
    const d = input[i3];
    const c0 = b;
    const c1 = 0.5 * (c - a);
    const c2 = a - 2.5 * b + 2 * c - 0.5 * d;
    const c3 = 0.5 * (d - a) + 1.5 * (b - c);
    out[i] = ((c3 * f + c2) * f + c1) * f + c0;
  }
  return out;
}

/** Pitch shift in semitones, preserving duration. */
export function pitchShift(channels: Float32Array[], semitones: number): Float32Array[] {
  if (Math.abs(semitones) < 1e-3) return channels.map((c) => c.slice());
  const factor = Math.pow(2, semitones / 12);
  const len = channels[0].length;
  const stretched = timeStretch(channels, factor);
  return stretched.map((c) => resample(c, len));
}

export function reverseChannels(channels: Float32Array[]): Float32Array[] {
  return channels.map((c) => {
    const out = new Float32Array(c.length);
    for (let i = 0; i < c.length; i++) out[i] = c[c.length - 1 - i];
    return out;
  });
}

export function sliceChannels(
  buffer: AudioBuffer,
  startSec: number,
  endSec: number,
): Float32Array[] {
  const sr = buffer.sampleRate;
  const s = Math.max(0, Math.floor(startSec * sr));
  const e = Math.min(buffer.length, Math.max(s + 1, Math.floor(endSec * sr)));
  const out: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    out.push(buffer.getChannelData(c).slice(s, e));
  }
  return out;
}

export function channelsToBuffer(
  ctx: BaseAudioContext,
  channels: Float32Array[],
  sampleRate: number,
): AudioBuffer {
  const len = Math.max(1, channels[0].length);
  const buf = ctx.createBuffer(channels.length, len, sampleRate);
  for (let c = 0; c < channels.length; c++) {
    buf.copyToChannel(channels[c] as Float32Array<ArrayBuffer>, c);
  }
  return buf;
}
